import React, { useState, useEffect, useContext, createContext, useCallback, useRef } from "react";
import {
  useAdminListApplications,
  useAdminUpdateApplication,
  useAdminListInquiries,
  useListCategories,
  useGetListing,
  useAdminUpdateListing,
  useAdminCreateListing,
  useAdminRequestListingImageUploadUrl,
  useAdminDeleteListing,
  useListMerchants,
  useAdminUpdateMerchant,
  getAdminListApplicationsQueryKey,
} from "@workspace/api-client-react";
import type { ApplicationStatusUpdateServiceMethodsItem } from "@workspace/api-client-react";
import {
  useAdminListLeads,
  useAdminUpdateLead,
  getAdminListLeadsQueryKey,
  type AdminLead,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { Lock, LogOut, CheckCircle, XCircle, Trash2, Download, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Settings2, Pencil, Plus, Star, X, ArrowUp, ArrowDown, Upload, MapPin, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/contexts/I18nContext";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

// ── Session context ────────────────────────────────────────────────────────────
// Provides a `logout` callback to all child tabs so that any 401 response
// (from raw fetch or React Query) can redirect the operator back to login.

const AdminSessionContext = createContext<{ logout: () => void }>({ logout: () => undefined });

function useAdminLogout() {
  return useContext(AdminSessionContext).logout;
}

// ──────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 15;
const SERVICE_METHOD_OPTIONS = [
  ["merchant_location", "Service at Merchant Location"],
  ["customer_location", "Service at Customer Location / Home Service"],
  ["physical_goods", "Physical Goods"],
  ["delivery", "Delivery Available"],
  ["pickup", "Pickup Available"],
  ["online_remote", "Online / Remote Service"],
] as const;

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  useEffect(() => {
    let active = true;
    fetch("/api/admin/session", { credentials: "same-origin" })
      .then((response) => {
        if (active) setIsAuthenticated(response.ok);
      })
      .catch(() => {
        if (active) setIsAuthenticated(false);
      });
    return () => { active = false; };
  }, []);

  // Subscribe to React Query query and mutation cache errors so any 401
  // immediately redirects the operator back to the login screen.
  const handleLogout = useCallback(async () => {
    await fetch("/api/admin/session", { method: "DELETE", credentials: "same-origin" }).catch(() => undefined);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const is401 = (error: unknown) => (error as { status?: number })?.status === 401;

    const unsubQuery = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === "updated" &&
        event.action?.type === "error" &&
        is401(event.action.error)
      ) {
        void handleLogout();
      }
    });

    const unsubMutation = queryClient.getMutationCache().subscribe((event) => {
      if (
        event.type === "updated" &&
        event.mutation?.state.status === "error" &&
        is401(event.mutation.state.error)
      ) {
        void handleLogout();
      }
    });

    return () => {
      unsubQuery();
      unsubMutation();
    };
  }, [isAuthenticated, queryClient, handleLogout]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    try {
      const response = await fetch("/api/admin/session", {
        method: "POST",
        credentials: "same-origin",
        headers: { Authorization: `Basic ${btoa(`admin:${password}`)}` },
      });
      setPassword("");
      if (!response.ok) {
        setIsAuthenticated(false);
        toast({ title: t("Invalid credentials"), variant: "destructive" });
        return;
      }
      setIsAuthenticated(true);
    } catch {
      setPassword("");
      setIsAuthenticated(false);
      toast({ title: t("Unable to sign in"), variant: "destructive" });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex-1 flex items-center justify-center bg-muted/20">
        <Card className="w-full max-w-md shadow-2xl border-border">
          <CardHeader className="text-center pb-2">
            <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-6 w-6" />
            </div>
            <CardTitle className="text-2xl">{t("Admin Login")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4 pt-4">
              <Input
                type="password"
                placeholder={t("Admin Password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
              />
              <Button type="submit" className="w-full h-12">{t("Login")}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AdminSessionContext.Provider value={{ logout: handleLogout }}>
      <div className="flex-1 flex flex-col bg-muted/10">
        <header className="bg-card border-b border-border p-4 flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" /> {t("Admin Panel")}
          </h1>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground">
            <LogOut className="h-4 w-4 mr-2" /> {t("Logout")}
          </Button>
        </header>

        <main className="flex-1 p-6 overflow-hidden flex flex-col">
          <Tabs defaultValue="leads" className="flex-1 flex flex-col">
            <TabsList className="grid w-full max-w-5xl grid-cols-7 mb-6">
              <TabsTrigger value="leads">{t("Deals & Leads")}</TabsTrigger>
              <TabsTrigger value="applications">{t("Applications")}</TabsTrigger>
              <TabsTrigger value="companies" data-testid="tab-companies">{t("Companies")}</TabsTrigger>
              <TabsTrigger value="listings">{t("Listings")}</TabsTrigger>
              <TabsTrigger value="inquiries">{t("Inquiries")}</TabsTrigger>
              <TabsTrigger value="demand">Demand</TabsTrigger>
              <TabsTrigger value="health">Health</TabsTrigger>
            </TabsList>

            <div className="flex-1 bg-card rounded-xl border border-border overflow-hidden flex flex-col">
              <TabsContent value="leads" className="flex-1 m-0 p-0 overflow-auto flex flex-col">
                <LeadsTab />
              </TabsContent>
              <TabsContent value="applications" className="flex-1 m-0 p-0 overflow-auto flex flex-col">
                <ApplicationsTab />
              </TabsContent>
              <TabsContent value="companies" className="flex-1 m-0 p-0 overflow-auto flex flex-col">
                <CompaniesTab />
              </TabsContent>
              <TabsContent value="listings" className="flex-1 m-0 p-0 overflow-auto flex flex-col">
                <ListingsTab />
              </TabsContent>
              <TabsContent value="inquiries" className="flex-1 m-0 p-0 overflow-auto flex flex-col">
                <InquiriesTab />
              </TabsContent>
              <TabsContent value="demand" className="flex-1 m-0 p-0 overflow-auto flex flex-col"><DemandTab /></TabsContent>
              <TabsContent value="health" className="flex-1 m-0 p-0 overflow-auto flex flex-col"><HealthTab /></TabsContent>
            </div>
          </Tabs>
        </main>
      </div>
    </AdminSessionContext.Provider>
  );
}

// ── Pagination controls ────────────────────────────────────────────────────────

function Pagination({ page, total, pageSize, onPage }: { page: number; total: number; pageSize: number; onPage: (p: number) => void }) {
  const { t } = useI18n();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20 shrink-0">
      <span className="text-sm text-muted-foreground">
        {t("Page")} {page} {t("of")} {totalPages}
      </span>
      <div className="flex gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

type DemandRequest = { id: number; referenceCode: string; requestText: string; categoryName: string | null; area: string; budgetAed: number | null; customerName: string; customerWhatsapp: string; customerEmail: string | null; status: string; internalNotes: string | null; createdAt: string };
type MarketplaceHealth = { activeMerchants: number; activeListings: number; liveApprovedDeals: number; totalSearches: number; zeroResultSearches: number; customerDemandRequests: number; dealRequests: number; merchantContactReveals: number; merchantApplications: number; leadToContactRate: number; demandFillRate: number; requestsByCategoryAndArea: { category: string; area: string; count: number }[] };
const adminHeaders = () => ({ "Content-Type": "application/json" });

function DemandTab() {
  const { toast } = useToast();
  const logout = useAdminLogout();
  const [rows, setRows] = useState<DemandRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/demand-requests${query ? `?q=${encodeURIComponent(query)}` : ""}`, { credentials: "same-origin", headers: adminHeaders() });
      if (response.status === 401) { void logout(); return; }
      if (!response.ok) throw new Error();
      setRows(await response.json());
    } catch { toast({ title: "Unable to load demand requests", variant: "destructive" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);
  const update = async (id: number, status: string) => {
    const response = await fetch(`/api/admin/demand-requests/${id}`, { method: "PATCH", credentials: "same-origin", headers: adminHeaders(), body: JSON.stringify({ status }) });
    if (response.status === 401) { void logout(); return; }
    if (!response.ok) { toast({ title: "Update failed", variant: "destructive" }); return; }
    await load();
  };
  return <div className="flex h-full flex-col">
    <div className="flex gap-2 border-b bg-muted/30 p-3"><Input className="max-w-sm" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search private requests" /><Button variant="outline" onClick={() => void load()}>Search</Button></div>
    <div className="flex-1 overflow-auto">{loading ? <div className="p-8 text-center">Loading…</div> : <Table><TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Request</TableHead><TableHead>Customer (private)</TableHead><TableHead>Area</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader><TableBody>
      {rows.map((row) => <TableRow key={row.id}><TableCell className="font-mono text-primary">{row.referenceCode}</TableCell><TableCell><div className="max-w-xs">{row.requestText}</div>{row.budgetAed ? <div className="text-xs text-muted-foreground">AED {row.budgetAed}</div> : null}</TableCell><TableCell><div>{row.customerName}</div><div className="text-xs text-muted-foreground">{row.customerWhatsapp}</div></TableCell><TableCell>{row.area}</TableCell><TableCell><Select value={row.status} onValueChange={(value) => void update(row.id, value)}><SelectTrigger className="w-36"><SelectValue /></SelectTrigger><SelectContent>{["new","searching","merchant_found","customer_contacted","completed","closed"].map((status) => <SelectItem key={status} value={status}>{status.replaceAll("_", " ")}</SelectItem>)}</SelectContent></Select></TableCell><TableCell className="whitespace-nowrap text-sm text-muted-foreground">{new Date(row.createdAt).toLocaleDateString()}</TableCell></TableRow>)}
      {!rows.length ? <TableRow><TableCell colSpan={6} className="p-8 text-center text-muted-foreground">No demand requests yet.</TableCell></TableRow> : null}
    </TableBody></Table>}</div>
  </div>;
}

function HealthTab() {
  const [data, setData] = useState<MarketplaceHealth | null>(null);
  const [error, setError] = useState(false);
  const logout = useAdminLogout();
  useEffect(() => {
    fetch("/api/admin/marketplace-health", { credentials: "same-origin", headers: adminHeaders() })
      .then(async (r) => {
        if (r.status === 401) { void logout(); return; }
        if (!r.ok) throw new Error();
        setData(await r.json());
      })
      .catch(() => setError(true));
  }, []);
  if (error) return <div className="p-8 text-center text-destructive">Unable to load marketplace health.</div>;
  if (!data) return <div className="p-8 text-center">Loading…</div>;
  const cards = [["Live merchants", data.activeMerchants], ["Live listings", data.activeListings], ["Approved deals", data.liveApprovedDeals], ["Demand requests", data.customerDemandRequests], ["Contact reveals", data.merchantContactReveals], ["Merchant applications", data.merchantApplications], ["Searches", data.totalSearches], ["Zero-result searches", data.zeroResultSearches]];
  return <div className="space-y-6 overflow-auto p-6"><p className="text-sm text-muted-foreground">Privacy-safe operational totals only. Search text and customer contact details are not included.</p><div className="grid grid-cols-2 gap-4 md:grid-cols-4">{cards.map(([label, value]) => <Card key={String(label)}><CardContent className="p-4"><p className="text-xs text-muted-foreground">{label}</p><p className="mt-1 text-2xl font-bold">{value}</p></CardContent></Card>)}</div><div className="grid gap-4 md:grid-cols-2"><Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Lead-to-contact rate</p><p className="text-3xl font-bold">{data.leadToContactRate}%</p></CardContent></Card><Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Demand fill rate</p><p className="text-3xl font-bold">{data.demandFillRate}%</p></CardContent></Card></div><Card><CardHeader><CardTitle className="text-base">Demand by category & area</CardTitle></CardHeader><CardContent>{data.requestsByCategoryAndArea.length ? <Table><TableHeader><TableRow><TableHead>Category</TableHead><TableHead>Area</TableHead><TableHead>Requests</TableHead></TableRow></TableHeader><TableBody>{data.requestsByCategoryAndArea.map((row, index) => <TableRow key={`${row.category}-${row.area}-${index}`}><TableCell>{row.category}</TableCell><TableCell>{row.area}</TableCell><TableCell>{row.count}</TableCell></TableRow>)}</TableBody></Table> : <p className="text-sm text-muted-foreground">No demand data yet.</p>}</CardContent></Card></div>;
}

// ── Leads tab ─────────────────────────────────────────────────────────────────

const LEAD_STATUSES = ["new", "contacted", "qualified", "converted", "lost"] as const;
const PAYMENT_STATUSES = ["not_due", "due", "paid"] as const;

function LeadsTab() {
  const { data: leads, isLoading } = useAdminListLeads({
    refetchInterval: 15000,
    queryKey: getAdminListLeadsQueryKey(),
  });
  const updateLead = useAdminUpdateLead();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [page, setPage] = useState(1);

  const handleStatusChange = (id: number, status: AdminLead["status"]) => {
    updateLead.mutate(
      { id, data: { status } },
      {
        onSuccess: () => {
          toast({ title: `Lead updated → ${status}` });
          queryClient.invalidateQueries({ queryKey: getAdminListLeadsQueryKey() });
        },
        onError: () => toast({ title: "Update failed", variant: "destructive" }),
      },
    );
  };

  const handlePaymentChange = (id: number, paymentStatus: AdminLead["paymentStatus"]) => {
    updateLead.mutate(
      { id, data: { paymentStatus } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getAdminListLeadsQueryKey() });
        },
      },
    );
  };

  const logout = useAdminLogout();

  const handleExportCsv = async () => {
    try {
      const res = await fetch("/api/admin/leads/export.csv", { credentials: "same-origin" });
      if (res.status === 401) { void logout(); return; }
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `souq-crypto-leads-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "CSV export failed", variant: "destructive" });
    }
  };

  if (isLoading) return <div className="p-8 text-center">{t("Loading...")}</div>;

  const allLeads = leads ?? [];
  const total = allLeads.length;
  const pageLeads = allLeads.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30 shrink-0">
        <span className="text-sm font-medium text-muted-foreground">
          {total} lead{total !== 1 ? "s" : ""}
        </span>
        <Button size="sm" variant="outline" onClick={handleExportCsv} className="gap-2">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader className="bg-muted/50 sticky top-0">
            <TableRow>
              <TableHead className="w-28">Ref Code</TableHead>
              <TableHead>{t("Listing")}</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>{t("Date")}</TableHead>
              <TableHead className="w-40">Lead Status</TableHead>
              <TableHead>Model</TableHead>
              <TableHead className="w-36">Payment</TableHead>
              <TableHead className="w-24">{t("Notified")}</TableHead>
              <TableHead className="max-w-[180px]">Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageLeads.map((lead) => (
              <TableRow key={lead.id}>
                <TableCell>
                  <span className="font-mono text-sm font-semibold text-primary">{lead.referenceCode}</span>
                </TableCell>
                <TableCell>
                  <div className="font-semibold text-sm max-w-[160px] truncate">{lead.listingTitle}</div>
                  <div className="text-xs text-muted-foreground">{lead.merchantName}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm font-medium">{lead.customerName}</div>
                  {lead.customerWhatsapp ? (
                    <a
                      href={(() => {
                        const num = lead.customerWhatsapp.replace(/\D/g, "");
                        const msg = [
                          `Hi ${lead.customerName}, this is Souq Crypto.`,
                          `We received your request (ref: ${lead.referenceCode}) for "${lead.listingTitle}" with ${lead.merchantName}.`,
                          `We'd like to follow up and help connect you with the merchant.`,
                          "Source: Souq Crypto",
                        ].join("\n");
                        return buildWhatsAppUrl(num, msg);
                      })()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#25D366] hover:underline font-medium"
                      data-testid={`link-lead-whatsapp-${lead.id}`}
                    >
                      {lead.customerWhatsapp}
                    </a>
                  ) : null}
                  {lead.customerEmail && (
                    <div className="text-xs text-muted-foreground">{lead.customerEmail}</div>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                  {new Date(lead.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Select
                    value={lead.status}
                    onValueChange={(v) => handleStatusChange(lead.id, v as AdminLead["status"])}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="text-xs capitalize">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="text-xs text-muted-foreground">
                    {lead.commercialModel ?? "—"}
                    {lead.feeValue ? ` · ${lead.feeValue}` : ""}
                  </div>
                  {lead.commissionDue != null && (
                    <div className="text-xs font-medium">AED {lead.commissionDue}</div>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    value={lead.paymentStatus}
                    onValueChange={(v) => handlePaymentChange(lead.id, v as AdminLead["paymentStatus"])}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="text-xs">
                          {s.replace("_", " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  {lead.notificationSent ? (
                    <Badge variant="outline" className="border-green-500 text-green-600 text-xs">
                      {t("Notified")}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-400 text-amber-600 text-xs">
                      Pending
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-xs text-muted-foreground max-w-[160px] line-clamp-2">
                    {lead.adminNotes ?? "—"}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {allLeads.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  No leads yet. They'll appear here when buyers submit the "Request This Deal" form.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPage={setPage} />
    </>
  );
}

// ── Applications tab ──────────────────────────────────────────────────────────

function ApplicationsTab() {
  const { data: apps, isLoading } = useAdminListApplications(undefined, {
    query: { refetchInterval: 10000, queryKey: getAdminListApplicationsQueryKey() },
  });
  const updateApp = useAdminUpdateApplication();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");

  useEffect(() => { setPage(1); }, [searchQuery, statusFilter]);

  const handleUpdate = (id: number, status: "approved" | "rejected" | "pending", serviceMethods?: ApplicationStatusUpdateServiceMethodsItem[]) => {
    const statusTitle: Record<string, string> = {
      approved: t("Application approved"),
      rejected: t("Application rejected"),
      pending: t("Application pending"),
    };
    updateApp.mutate(
      { id, data: { status, ...(serviceMethods ? { serviceMethods } : {}) } },
      {
        onSuccess: (app) => {
          const converted = status === "approved" && app.convertedListingId;
          toast({
            title: converted ? t("Application approved — draft listing created") : statusTitle[status],
            description: converted ? `Draft listing #${app.convertedListingId} is unpublished and ready for review.` : undefined,
          });
          queryClient.invalidateQueries({ queryKey: ["/api/admin/applications"] });
          queryClient.invalidateQueries({ queryKey: ["/api/listings"] });
        },
        onError: (error) => {
          toast({
            title: t("Unable to update application"),
            description: error instanceof Error ? error.message : undefined,
            variant: "destructive",
          });
        },
      },
    );
  };

  if (isLoading) return <div className="p-8 text-center">{t("Loading...")}</div>;

  const rawApps = apps ?? [];
  const q = searchQuery.trim().toLowerCase();
  const allApps = rawApps.filter((app) => {
    if (statusFilter !== "all" && app.status !== statusFilter) return false;
    if (q && !app.businessName.toLowerCase().includes(q) && !app.email.toLowerCase().includes(q)) return false;
    return true;
  });
  const total = allApps.length;
  const pageApps = allApps.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border bg-muted/30 shrink-0">
        <Input
          className="h-8 max-w-xs text-sm"
          placeholder={t("Search by business or email…")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="h-8 w-36 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("All statuses")}</SelectItem>
            <SelectItem value="pending">{t("Pending")}</SelectItem>
            <SelectItem value="approved">{t("Approved")}</SelectItem>
            <SelectItem value="rejected">{t("Rejected")}</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">
          {allApps.length} / {rawApps.length}
        </span>
      </div>
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader className="bg-muted/50 sticky top-0">
            <TableRow>
              <TableHead>{t("Business")}</TableHead>
              <TableHead>{t("Contact")}</TableHead>
              <TableHead>{t("Category/Area")}</TableHead>
              <TableHead>{t("Plan")}</TableHead>
              <TableHead>{t("Service methods")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("Notified")}</TableHead>
              <TableHead className="text-right">{t("Actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageApps.map((app) => (
              <TableRow key={app.id}>
                <TableCell>
                  <div className="font-semibold">{app.businessName}</div>
                  <div className="text-xs text-muted-foreground truncate max-w-[200px]">{app.description}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1 max-w-56">
                    {(app.serviceMethods ?? []).map((method) => (
                      <Badge key={method} variant="outline" className="text-[10px]">
                        {t(serviceMethodLabel(method) as Parameters<typeof t>[0])}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-2 space-y-1 rounded-md border p-2">
                    {SERVICE_METHOD_OPTIONS.map(([value, label]) => {
                      const selected = (app.serviceMethods ?? []).includes(value);
                      return (
                        <label key={value} className="flex cursor-pointer items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              const current = app.serviceMethods ?? [];
                              const updated = selected ? current.filter((method) => method !== value) : [...current, value];
                              handleUpdate(app.id, app.status as "approved" | "rejected" | "pending", updated as ApplicationStatusUpdateServiceMethodsItem[]);
                            }}
                          />
                          {t(label)}
                        </label>
                      );
                    })}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{app.contactPerson}</div>
                  <div className="text-xs text-muted-foreground">{app.email}</div>
                  <div className="text-xs text-muted-foreground">{app.phone}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="mb-1">{app.category}</Badge>
                  <div className="text-xs text-muted-foreground">{app.area}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className={app.planType === "featured" ? "bg-accent/20 text-accent-foreground" : ""}>
                    {app.planType}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      app.status === "approved"
                        ? "border-green-500 text-green-500"
                        : app.status === "rejected"
                        ? "border-red-500 text-red-500"
                        : "border-amber-500 text-amber-500"
                    }
                  >
                    {t(app.status as "approved" | "rejected" | "pending")}
                  </Badge>
                  {app.convertedListingId && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Draft #{app.convertedListingId} · Merchant #{app.convertedMerchantId}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {app.webhookSentAt ? (
                    <Badge variant="outline" className="border-green-500 text-green-600 text-xs">
                      {t("Notified")}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-400 text-amber-600 text-xs">
                      {t("Webhook Pending")}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  {app.status !== "approved" && (
                    <Button
                      size="icon"
                      variant="ghost"
                      title={t("Approve")}
                      className="text-green-500 hover:text-green-600 hover:bg-green-50"
                      onClick={() => handleUpdate(app.id, "approved")}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                  )}
                  {app.status !== "rejected" && (
                    <Button
                      size="icon"
                      variant="ghost"
                      title={t("Reject")}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleUpdate(app.id, "rejected")}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {allApps.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {t("No applications found.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPage={setPage} />
    </>
  );
}

function serviceMethodLabel(method: string) {
  return ({
    merchant_location: "Service at Merchant Location",
    customer_location: "Service at Customer Location / Home Service",
    physical_goods: "Physical Goods",
    delivery: "Delivery Available",
    pickup: "Pickup Available",
    online_remote: "Online / Remote Service",
  } as Record<string, string>)[method] ?? method;
}

// ── Pricing config panel ──────────────────────────────────────────────────────

type ListingPricing = {
  commercialModel: "lead_fee" | "commission" | null;
  feeValue: number | null;
  adminNotes: string | null;
};

function PricingConfigPanel({ listingId }: { listingId: number }) {
  const { toast } = useToast();
  const logout = useAdminLogout();
  const [pricing, setPricing] = useState<ListingPricing>({ commercialModel: null, feeValue: null, adminNotes: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/admin/listings/${listingId}/pricing`, { credentials: "same-origin" })
      .then(async (r) => {
        if (r.status === 401) { void logout(); return; }
        if (!r.ok) throw new Error();
        const data = await r.json();
        if (data) setPricing({ commercialModel: data.commercialModel ?? null, feeValue: data.feeValue ?? null, adminNotes: data.adminNotes ?? null });
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [listingId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await fetch(`/api/admin/listings/${listingId}/pricing`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          commercialModel: pricing.commercialModel || null,
          feeValue: pricing.feeValue,
          adminNotes: pricing.adminNotes || null,
        }),
      });
      if (r.status === 401) { void logout(); return; }
      if (!r.ok) throw new Error();
      toast({ title: "Pricing config saved" });
    } catch {
      toast({ title: "Failed to save pricing config", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-4 py-3 text-sm text-muted-foreground">Loading…</div>;

  const isFlat = pricing.commercialModel === "lead_fee";
  const isPercent = pricing.commercialModel === "commission";

  return (
    <div className="bg-muted/20 border-t border-border px-4 py-3 grid grid-cols-1 gap-3 sm:grid-cols-4 sm:gap-4 items-end">
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Commercial Model</label>
        <Select
          value={pricing.commercialModel ?? "none"}
          onValueChange={(v) => setPricing((p) => ({ ...p, commercialModel: v === "none" ? null : v as "lead_fee" | "commission" }))}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Not set" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none" className="text-xs">Not set</SelectItem>
            <SelectItem value="lead_fee" className="text-xs">Flat fee per lead</SelectItem>
            <SelectItem value="commission" className="text-xs">% Commission</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">
          {isPercent ? "Commission %" : "Fee (AED)"}
        </label>
        <Input
          type="number"
          min={0}
          step={isPercent ? 0.1 : 1}
          placeholder={isFlat ? "e.g. 250" : isPercent ? "e.g. 5" : "—"}
          disabled={!pricing.commercialModel}
          value={pricing.feeValue ?? ""}
          onChange={(e) => setPricing((p) => ({ ...p, feeValue: e.target.value === "" ? null : parseFloat(e.target.value) }))}
          className="h-8 text-xs"
        />
        {isFlat && pricing.feeValue != null && (
          <p className="text-[10px] text-muted-foreground">AED {pricing.feeValue} charged per new lead</p>
        )}
        {isPercent && pricing.feeValue != null && (
          <p className="text-[10px] text-muted-foreground">{pricing.feeValue}% of deal value — set manually on each lead</p>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Admin Notes</label>
        <Input
          placeholder="Optional notes"
          value={pricing.adminNotes ?? ""}
          onChange={(e) => setPricing((p) => ({ ...p, adminNotes: e.target.value || null }))}
          className="h-8 text-xs"
        />
      </div>

      <div>
        <Button size="sm" onClick={handleSave} disabled={saving} className="w-full h-8 text-xs">
          <Settings2 className="h-3 w-3 mr-1" />
          {saving ? "Saving…" : "Save Pricing"}
        </Button>
      </div>
    </div>
  );
}

function MerchantLocationPanel({
  merchantId,
  merchantName,
  initialLat,
  initialLng,
}: {
  merchantId: number;
  merchantName: string;
  initialLat: number | null;
  initialLng: number | null;
}) {
  const { toast } = useToast();
  const updateMerchant = useAdminUpdateMerchant();
  // Initialise from the coordinates already embedded in the admin listings row
  // so this panel works for unpublished merchants too (no extra fetch needed).
  const [lat, setLat] = useState<string>(initialLat != null ? String(initialLat) : "");
  const [lng, setLng] = useState<string>(initialLng != null ? String(initialLng) : "");
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    const latitude = lat.trim() === "" ? null : parseFloat(lat);
    const longitude = lng.trim() === "" ? null : parseFloat(lng);
    if (latitude !== null && (Number.isNaN(latitude) || latitude < -90 || latitude > 90)) {
      toast({ title: "Latitude must be between −90 and 90", variant: "destructive" });
      return;
    }
    if (longitude !== null && (Number.isNaN(longitude) || longitude < -180 || longitude > 180)) {
      toast({ title: "Longitude must be between −180 and 180", variant: "destructive" });
      return;
    }
    setSaving(true);
    // Send null explicitly so the server clears the column and the map falls
    // back to the area-centre approximation, rather than leaving stale values.
    updateMerchant.mutate(
      { id: merchantId, data: { latitude, longitude } },
      {
        onSuccess: () => {
          toast({ title: "Merchant location saved" });
          setSaving(false);
        },
        onError: () => {
          toast({ title: "Failed to save location", variant: "destructive" });
          setSaving(false);
        },
      },
    );
  };

  return (
    <div className="bg-muted/20 border-t border-border px-4 py-3 grid grid-cols-1 gap-3 sm:grid-cols-4 sm:gap-4 items-end">
      <div className="sm:col-span-4 text-xs font-medium text-muted-foreground flex items-center gap-1">
        <MapPin className="h-3 w-3" />
        {merchantName} — exact location (leave blank to use area centre fallback)
      </div>
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Latitude</label>
        <Input
          type="number"
          step="any"
          min={-90}
          max={90}
          placeholder="e.g. 25.2048"
          value={lat}
          onChange={(e) => setLat(e.target.value)}
          className="h-8 text-xs"
        />
        <p className="text-[10px] text-muted-foreground">−90 to 90</p>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Longitude</label>
        <Input
          type="number"
          step="any"
          min={-180}
          max={180}
          placeholder="e.g. 55.2708"
          value={lng}
          onChange={(e) => setLng(e.target.value)}
          className="h-8 text-xs"
        />
        <p className="text-[10px] text-muted-foreground">−180 to 180</p>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Preview</label>
        {lat && lng ? (
          <a
            href={`https://maps.google.com/?q=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-primary underline block"
          >
            View on Google Maps ↗
          </a>
        ) : (
          <p className="text-[10px] text-muted-foreground">Enter coordinates to preview</p>
        )}
      </div>
      <div>
        <Button size="sm" onClick={handleSave} disabled={saving} className="w-full h-8 text-xs">
          <MapPin className="h-3 w-3 mr-1" />
          {saving ? "Saving…" : "Save Location"}
        </Button>
      </div>
    </div>
  );
}
function ListingEditDialog({ listingId, onClose, onSaved }: { listingId: number; onClose: () => void; onSaved?: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: categories } = useListCategories();
  const { data: detail, isLoading } = useGetListing(String(listingId));
  const updateListing = useAdminUpdateListing();
  const requestUploadUrl = useAdminRequestListingImageUploadUrl();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [listingType, setListingType] = useState<"product" | "service">("product");
  const [description, setDescription] = useState("");
  const [priceAed, setPriceAed] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (detail && !initialized) {
      setTitle(detail.title);
      setCategoryId(String(detail.categoryId));
      setListingType(detail.type === "service" ? "service" : "product");
      setDescription(detail.description ?? "");
      setPriceAed(detail.priceAed != null ? String(detail.priceAed) : "");
      const imgs = (detail.images?.length ? detail.images : [detail.imageUrl]).filter((u): u is string => typeof u === "string" && u.length > 0);
      setImages(imgs);
      const idx = detail.imageUrl ? imgs.indexOf(detail.imageUrl) : 0;
      setCoverIndex(idx >= 0 ? idx : 0);
      setInitialized(true);
    }
  }, [detail, initialized]);

  const uploadFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const selected = Array.from(files);
    if (replacingIndex === null && images.length + selected.length > 5) {
      toast({ title: t("You can upload up to 5 photos per listing"), description: t(`You can add ${Math.max(0, 5 - images.length)} more.`), variant: "destructive" });
      return;
    }
    const invalid = selected.find((file) => !["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024);
    if (invalid) {
      toast({ title: t("Invalid image"), description: t("Use JPEG, PNG, or WebP images up to 10 MB."), variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of selected) {
        const ticket = await requestUploadUrl.mutateAsync({
          data: { name: file.name, size: file.size, contentType: file.type as "image/jpeg" | "image/png" | "image/webp" },
        });
        const response = await fetch(ticket.uploadURL, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!response.ok) throw new Error("upload_failed");
        const publish = await fetch("/api/admin/listing-images/publish", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ objectPath: ticket.objectPath }),
        });
        if (!publish.ok) throw new Error("publish_failed");
        const published = await publish.json() as { objectPath: string };
        uploaded.push(`/api/storage${published.objectPath}`);
      }
      if (replacingIndex !== null) {
        setImages((prev) => prev.map((image, index) => index === replacingIndex ? uploaded[0] ?? image : image));
        setReplacingIndex(null);
      } else {
        setImages((prev) => [...prev, ...uploaded]);
      }
      toast({ title: selected.length === 1 ? t("Photo uploaded") : t("Photos uploaded") });
    } catch {
      toast({ title: t("Photo upload failed"), description: t("Please try again."), variant: "destructive" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (index: number) => {
    if (images.length <= 1) {
      toast({ title: t("A listing needs at least one image"), variant: "destructive" });
      return;
    }
    setImages((prev) => prev.filter((_, i) => i !== index));
    setCoverIndex((prev) => {
      if (index === prev) return 0;
      return index < prev ? prev - 1 : prev;
    });
  };

  const moveImage = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= images.length) return;
    setImages((prev) => {
      const next = [...prev];
      const a = next[index];
      const b = next[target];
      if (a === undefined || b === undefined) return prev;
      next[index] = b;
      next[target] = a;
      return next;
    });
    setCoverIndex((prev) => (prev === index ? target : prev === target ? index : prev));
  };

  const handleSave = () => {
    if (title.trim().length < 2) {
      toast({ title: t("Title is required"), variant: "destructive" });
      return;
    }
    if (!categoryId) {
      toast({ title: t("Category is required"), variant: "destructive" });
      return;
    }
    if (images.length === 0) {
      toast({ title: t("A listing needs at least one image"), variant: "destructive" });
      return;
    }
    const price = priceAed.trim() === "" ? null : Number(priceAed);
    if (price !== null && (!Number.isFinite(price) || price < 0)) {
      toast({ title: t("Price must be a positive number"), variant: "destructive" });
      return;
    }
    updateListing.mutate(
      {
        id: listingId,
        data: {
          title: title.trim(),
          type: listingType,
          categoryId: Number(categoryId),
          description: description.trim(),
          priceAed: price,
          imageUrl: images[coverIndex] ?? images[0],
          images,
        },
      },
      {
        onSuccess: () => {
          toast({ title: t("Listing updated") });
          queryClient.invalidateQueries({ queryKey: ["/api/listings"] });
          onSaved?.();
          onClose();
        },
        onError: (error: unknown) => {
          const message = (error as { data?: { error?: string } })?.data?.error;
          toast({ title: t("Failed to update listing"), description: message, variant: "destructive" });
        },
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("Edit listing")}</DialogTitle>
        </DialogHeader>
        {isLoading || !detail ? (
          <div className="py-10 text-center text-muted-foreground">{t("Loading...")}</div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="edit-listing-title">{t("Title")}</label>
              <Input
                id="edit-listing-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="input-edit-listing-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("Category")}</label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger data-testid="select-edit-listing-category">
                    <SelectValue placeholder={t("Select category")} />
                  </SelectTrigger>
                  <SelectContent>
                    {(categories ?? []).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("Type")}</label>
                <Select value={listingType} onValueChange={(v) => setListingType(v as "product" | "service")}>
                  <SelectTrigger data-testid="select-edit-listing-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">{t("Product")}</SelectItem>
                    <SelectItem value="service">{t("Service")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="edit-listing-price">{t("Price (AED, optional)")}</label>
              <Input
                id="edit-listing-price"
                type="number"
                min="0"
                value={priceAed}
                onChange={(e) => setPriceAed(e.target.value)}
                data-testid="input-edit-listing-price"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="edit-listing-description">{t("Description")}</label>
              <textarea
                id="edit-listing-description"
                className="w-full min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="input-edit-listing-description"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t("Photos")}</label>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">{images.length}/5 {t("photos")}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple={replacingIndex === null}
                    className="hidden"
                    onChange={(event) => { void uploadFiles(event.target.files); }}
                    data-testid="input-listing-photo-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-1"
                    disabled={isUploading || images.length >= 5}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="btn-upload-listing-photo"
                  >
                    <Upload className="h-4 w-4" />
                    {isUploading ? t("Uploading…") : replacingIndex === null ? t("Upload photos") : t("Choose replacement")}
                  </Button>
                </div>
                {images.map((url, index) => (
                  <div key={url} className="flex items-center gap-2 rounded-lg border border-border p-2">
                    <img
                      src={url}
                      alt=""
                      className="h-12 w-12 shrink-0 rounded-md border border-border bg-muted object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.opacity = "0.3"; }}
                    />
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" dir="ltr">{url}</span>
                    <Button
                      type="button"
                      size="sm"
                      variant={index === coverIndex ? "secondary" : "ghost"}
                      className="h-7 gap-1 px-2 text-xs"
                      onClick={() => setCoverIndex(index)}
                      title={t("Set as cover image")}
                      data-testid={`btn-cover-image-${index}`}
                    >
                      <Star className={`h-3.5 w-3.5 ${index === coverIndex ? "fill-current" : ""}`} />
                      {index === coverIndex ? t("Cover") : ""}
                    </Button>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={index === 0} onClick={() => moveImage(index, -1)} title={t("Move up")}>
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={index === images.length - 1} onClick={() => moveImage(index, 1)} title={t("Move down")}>
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      disabled={isUploading}
                      onClick={() => { setReplacingIndex(index); fileInputRef.current?.click(); }}
                      title={t("Replace image")}
                      data-testid={`btn-replace-image-${index}`}
                    >
                      {t("Replace")}
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      onClick={() => removeImage(index)}
                      title={t("Remove image")}
                      data-testid={`btn-remove-image-${index}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{t("Upload JPEG, PNG, or WebP photos up to 10 MB. You can replace, reorder, remove, or star a photo as the listing's main image.")}</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>{t("Cancel")}</Button>
              <Button type="button" onClick={handleSave} disabled={updateListing.isPending || isUploading} data-testid="btn-save-listing-edit">
                {updateListing.isPending ? t("Saving…") : t("Save changes")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Listing create dialog ─────────────────────────────────────────────────────

function ListingCreateDialog({ onClose, lockedMerchant, onCreated }: {
  onClose: () => void;
  /** When set, the listing is created for this merchant and the merchant selector is hidden. */
  lockedMerchant?: { id: number; name: string; area: string; categoryId: number; categoryName: string };
  onCreated?: () => void;
}) {
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: categories } = useListCategories();
  const { data: merchantsResult } = useListMerchants({ limit: 100 });
  const createListing = useAdminCreateListing();
  const requestUploadUrl = useAdminRequestListingImageUploadUrl();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [type, setType] = useState<"product" | "service">(
    lockedMerchant && /service|repair|clean|salon|clinic|fitness|legal|consult/i.test(lockedMerchant.categoryName) ? "service" : "product",
  );
  const [categoryId, setCategoryId] = useState<string>(lockedMerchant ? String(lockedMerchant.categoryId) : "");
  const [merchantId, setMerchantId] = useState<string>(lockedMerchant ? String(lockedMerchant.id) : "");
  const [area, setArea] = useState(lockedMerchant?.area ?? "");
  const [description, setDescription] = useState("");
  const [priceAed, setPriceAed] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const uploadFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const selected = Array.from(files);
    if (images.length + selected.length > 5) {
      toast({ title: t("You can upload up to 5 photos per listing"), description: t(`You can add ${Math.max(0, 5 - images.length)} more.`), variant: "destructive" });
      return;
    }
    const invalid = selected.find((file) => !["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024);
    if (invalid) {
      toast({ title: t("Invalid image"), description: t("Use JPEG, PNG, or WebP images up to 10 MB."), variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of selected) {
        const ticket = await requestUploadUrl.mutateAsync({
          data: { name: file.name, size: file.size, contentType: file.type as "image/jpeg" | "image/png" | "image/webp" },
        });
        const response = await fetch(ticket.uploadURL, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!response.ok) throw new Error("upload_failed");
        const publish = await fetch("/api/admin/listing-images/publish", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ objectPath: ticket.objectPath }),
        });
        if (!publish.ok) throw new Error("publish_failed");
        const published = await publish.json() as { objectPath: string };
        uploaded.push(`/api/storage${published.objectPath}`);
      }
      setImages((prev) => [...prev, ...uploaded]);
      toast({ title: selected.length === 1 ? t("Photo uploaded") : t("Photos uploaded") });
    } catch {
      toast({ title: t("Photo upload failed"), description: t("Please try again."), variant: "destructive" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setCoverIndex((prev) => {
      if (index === prev) return 0;
      return index < prev ? prev - 1 : prev;
    });
  };

  const handleCreate = () => {
    if (title.trim().length < 2) {
      toast({ title: t("Title is required"), variant: "destructive" });
      return;
    }
    if (!categoryId) {
      toast({ title: t("Category is required"), variant: "destructive" });
      return;
    }
    if (!merchantId) {
      toast({ title: t("Merchant is required"), variant: "destructive" });
      return;
    }
    if (area.trim().length < 2) {
      toast({ title: t("Area is required"), variant: "destructive" });
      return;
    }
    if (description.trim().length < 2) {
      toast({ title: t("Description is required"), variant: "destructive" });
      return;
    }
    if (images.length === 0) {
      toast({ title: t("A listing needs at least one image"), variant: "destructive" });
      return;
    }
    const price = priceAed.trim() === "" ? undefined : Number(priceAed);
    if (price !== undefined && (!Number.isFinite(price) || price < 0)) {
      toast({ title: t("Price must be a positive number"), variant: "destructive" });
      return;
    }
    createListing.mutate(
      {
        data: {
          title: title.trim(),
          type,
          categoryId: Number(categoryId),
          merchantId: Number(merchantId),
          area: area.trim(),
          description: description.trim(),
          imageUrl: images[coverIndex] ?? images[0]!,
          images,
          ...(price !== undefined ? { priceAed: price } : {}),
        },
      },
      {
        onSuccess: () => {
          toast({ title: t("Listing created") });
          queryClient.invalidateQueries({ queryKey: ["/api/listings"] });
          onCreated?.();
          onClose();
        },
        onError: (error: unknown) => {
          const message = (error as { data?: { error?: string } })?.data?.error;
          toast({ title: t("Failed to create listing"), description: message, variant: "destructive" });
        },
      },
    );
  };

  const merchants = merchantsResult ?? [];

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("New listing")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="create-listing-title">{t("Title")}</label>
            <Input
              id="create-listing-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="input-create-listing-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("Type")}</label>
              <Select value={type} onValueChange={(v) => setType(v as "product" | "service")}>
                <SelectTrigger data-testid="select-create-listing-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">{t("Product")}</SelectItem>
                  <SelectItem value="service">{t("Service")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("Category")}</label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger data-testid="select-create-listing-category">
                  <SelectValue placeholder={t("Select category")} />
                </SelectTrigger>
                <SelectContent>
                  {(categories ?? []).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {lockedMerchant ? (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("Merchant")}</label>
              <Input value={lockedMerchant.name} disabled data-testid="input-create-listing-merchant-locked" />
            </div>
          ) : (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("Merchant")}</label>
              <Select value={merchantId} onValueChange={setMerchantId}>
                <SelectTrigger data-testid="select-create-listing-merchant">
                  <SelectValue placeholder={t("Select merchant")} />
                </SelectTrigger>
                <SelectContent>
                  {merchants.map((m) => (
                    <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="create-listing-area">{t("Area")}</label>
              <Input
                id="create-listing-area"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                data-testid="input-create-listing-area"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="create-listing-price">{t("Price (AED, optional)")}</label>
              <Input
                id="create-listing-price"
                type="number"
                min="0"
                value={priceAed}
                onChange={(e) => setPriceAed(e.target.value)}
                data-testid="input-create-listing-price"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="create-listing-description">{t("Description")}</label>
            <textarea
              id="create-listing-description"
              className="w-full min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="input-create-listing-description"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t("Photos")}</label>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">{images.length}/5 {t("photos")}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={(event) => { void uploadFiles(event.target.files); }}
                  data-testid="input-create-listing-photo-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="gap-1"
                  disabled={isUploading || images.length >= 5}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="btn-create-upload-listing-photo"
                >
                  <Upload className="h-4 w-4" />
                  {isUploading ? t("Uploading…") : t("Upload photos")}
                </Button>
              </div>
              {images.map((url, index) => (
                <div key={url} className="flex items-center gap-2 rounded-lg border border-border p-2">
                  <img
                    src={url}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-md border border-border bg-muted object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.opacity = "0.3"; }}
                  />
                  <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" dir="ltr">{url}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant={index === coverIndex ? "secondary" : "ghost"}
                    className="h-7 gap-1 px-2 text-xs"
                    onClick={() => setCoverIndex(index)}
                    title={t("Set as cover image")}
                    data-testid={`btn-create-cover-image-${index}`}
                  >
                    <Star className={`h-3.5 w-3.5 ${index === coverIndex ? "fill-current" : ""}`} />
                    {index === coverIndex ? t("Cover") : ""}
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive hover:bg-destructive/10"
                    onClick={() => removeImage(index)}
                    title={t("Remove image")}
                    data-testid={`btn-create-remove-image-${index}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{t("Upload JPEG, PNG, or WebP photos up to 10 MB. The first photo becomes the cover image unless you star another one.")}</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("Cancel")}</Button>
            <Button type="button" onClick={handleCreate} disabled={createListing.isPending || isUploading} data-testid="btn-save-listing-create">
              {createListing.isPending ? t("Saving…") : t("Create listing")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Listings tab ──────────────────────────────────────────────────────────────

type AdminListingRow = {
  id: number;
  title: string;
  merchantId: number;
  merchantName: string;
  categoryName: string;
  area: string;
  imageUrl: string;
  priceAed: number | null;
  isFeatured: boolean;
  isPublished: boolean;
  createdAt: string;
  merchantLatitude: number | null;
  merchantLongitude: number | null;
};

function ListingsTab() {
  const [adminListings, setAdminListings] = useState<AdminListingRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const logout = useAdminLogout();

  const loadAdminListings = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch("/api/admin/listings", { credentials: "same-origin" });
      if (res.status === 401) { void logout(); return; }
      if (!res.ok) throw new Error();
      const data = await res.json() as { listings: AdminListingRow[] };
      setAdminListings(data.listings);
    } catch {
      // leave previous data in place; table will show whatever was last loaded
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  useEffect(() => { void loadAdminListings(); }, [loadAdminListings]);

  const updateListing = useAdminUpdateListing();
  const deleteListing = useAdminDeleteListing();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [page, setPage] = useState(1);
  const [expandedPricing, setExpandedPricing] = useState<Set<number>>(new Set());
  const [expandedLocation, setExpandedLocation] = useState<Set<number>>(new Set());
  const [editingListingId, setEditingListingId] = useState<number | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [listingSearch, setListingSearch] = useState("");
  const [publishedFilter, setPublishedFilter] = useState<"all" | "published" | "unpublished">("all");
  const [featuredFilter, setFeaturedFilter] = useState<"all" | "featured" | "not-featured">("all");

  useEffect(() => { setPage(1); }, [listingSearch, publishedFilter, featuredFilter]);

  const togglePricing = (id: number) => {
    setExpandedPricing((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleLocation = (id: number) => {
    setExpandedLocation((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleToggleFeature = (id: number, currentVal: boolean) => {
    updateListing.mutate({ id, data: { isFeatured: !currentVal } }, {
      onSuccess: () => {
        toast({ title: t("Listing updated") });
        queryClient.invalidateQueries({ queryKey: ["/api/listings"] });
        void loadAdminListings();
      },
    });
  };

  const handleTogglePublish = (id: number, currentVal: boolean) => {
    updateListing.mutate({ id, data: { isPublished: !currentVal } }, {
      onSuccess: () => {
        toast({ title: t("Listing updated") });
        queryClient.invalidateQueries({ queryKey: ["/api/listings"] });
        void loadAdminListings();
      },
    });
  };

  const handleDelete = (id: number) => {
    if (!confirm(t("Are you sure you want to delete this listing?"))) return;
    deleteListing.mutate({ id }, {
      onSuccess: () => {
        toast({ title: t("Listing deleted") });
        queryClient.invalidateQueries({ queryKey: ["/api/listings"] });
        void loadAdminListings();
      },
    });
  };

  if (isLoading) return <div className="p-8 text-center">{t("Loading...")}</div>;

  const rawListings = adminListings;
  const lq = listingSearch.trim().toLowerCase();
  const allListings = rawListings.filter((listing) => {
    if (publishedFilter !== "all") {
      const isPublished = listing.isPublished !== false;
      if (publishedFilter === "published" && !isPublished) return false;
      if (publishedFilter === "unpublished" && isPublished) return false;
    }
    if (featuredFilter !== "all") {
      if (featuredFilter === "featured" && !listing.isFeatured) return false;
      if (featuredFilter === "not-featured" && listing.isFeatured) return false;
    }
    if (lq && !listing.title.toLowerCase().includes(lq) && !(listing.merchantName ?? "").toLowerCase().includes(lq)) return false;
    return true;
  });
  const total = allListings.length;
  const pageListings = allListings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border bg-muted/30 shrink-0">
        <Input
          className="h-8 max-w-xs text-sm"
          placeholder={t("Search by title or merchant…")}
          value={listingSearch}
          onChange={(e) => setListingSearch(e.target.value)}
        />
        <Select value={publishedFilter} onValueChange={(v) => setPublishedFilter(v as typeof publishedFilter)}>
          <SelectTrigger className="h-8 w-36 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("All listings")}</SelectItem>
            <SelectItem value="published">{t("Published")}</SelectItem>
            <SelectItem value="unpublished">{t("Unpublished")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={featuredFilter} onValueChange={(v) => setFeaturedFilter(v as typeof featuredFilter)}>
          <SelectTrigger className="h-8 w-36 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("All featured")}</SelectItem>
            <SelectItem value="featured">{t("Featured only")}</SelectItem>
            <SelectItem value="not-featured">{t("Not featured")}</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          {allListings.length} / {rawListings.length}
        </span>
        <div className="ml-auto">
          <Button
            size="sm"
            className="gap-1"
            onClick={() => setShowCreateDialog(true)}
            data-testid="btn-new-listing"
          >
            <Plus className="h-4 w-4" />
            {t("New listing")}
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader className="bg-muted/50 sticky top-0">
            <TableRow>
              <TableHead className="w-14">{t("Image")}</TableHead>
              <TableHead>{t("Title")}</TableHead>
              <TableHead>{t("Merchant")}</TableHead>
              <TableHead>{t("Category/Area")}</TableHead>
              <TableHead>{t("Featured")}</TableHead>
              <TableHead>{t("Published")}</TableHead>
              <TableHead>Pricing</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-right">{t("Actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageListings.map((listing) => (
              <React.Fragment key={listing.id}>
                <TableRow>
                  <TableCell className="p-2">
                    {listing.imageUrl ? (
                      <img
                        src={listing.imageUrl}
                        alt={listing.title}
                        className="w-10 h-10 object-cover rounded-md border border-border bg-muted"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-md border border-border bg-muted flex items-center justify-center text-muted-foreground text-xs">
                        —
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold max-w-[180px] truncate">{listing.title}</div>
                    <div className="text-xs text-muted-foreground">{listing.priceAed ? `${listing.priceAed} AED` : t("Ask price")}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{listing.merchantName}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="souq-category-badge mb-1">{listing.categoryName}</Badge>
                    <div className="text-xs text-muted-foreground">{listing.area}</div>
                  </TableCell>
                  <TableCell>
                    <Switch checked={listing.isFeatured} onCheckedChange={() => handleToggleFeature(listing.id, listing.isFeatured)} />
                  </TableCell>
                  <TableCell>
                    <Switch checked={listing.isPublished !== false} onCheckedChange={() => handleTogglePublish(listing.id, listing.isPublished !== false)} />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant={expandedPricing.has(listing.id) ? "secondary" : "outline"}
                      className="h-7 text-xs gap-1"
                      onClick={() => togglePricing(listing.id)}
                    >
                      {expandedPricing.has(listing.id) ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      Config
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant={expandedLocation.has(listing.id) ? "secondary" : "outline"}
                      className="h-7 text-xs gap-1"
                      onClick={() => toggleLocation(listing.id)}
                      title="Set merchant coordinates"
                    >
                      <MapPin className="h-3 w-3" />
                      {expandedLocation.has(listing.id) ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      size="icon"
                      variant="ghost"
                      title={t("Edit listing")}
                      onClick={() => setEditingListingId(listing.id)}
                      data-testid={`btn-edit-listing-${listing.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title={t("Delete listing")}
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => handleDelete(listing.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
                {expandedPricing.has(listing.id) && (
                  <TableRow>
                    <TableCell colSpan={9} className="p-0">
                      <PricingConfigPanel listingId={listing.id} />
                    </TableCell>
                  </TableRow>
                )}
                {expandedLocation.has(listing.id) && (
                  <TableRow>
                    <TableCell colSpan={9} className="p-0">
                      <MerchantLocationPanel merchantId={listing.merchantId} merchantName={listing.merchantName} initialLat={listing.merchantLatitude} initialLng={listing.merchantLongitude} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {allListings.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  {t("No listings found.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPage={setPage} />
      {editingListingId !== null && (
        <ListingEditDialog listingId={editingListingId} onClose={() => { setEditingListingId(null); void loadAdminListings(); }} />
      )}
      {showCreateDialog && (
        <ListingCreateDialog onClose={() => { setShowCreateDialog(false); void loadAdminListings(); }} />
      )}
    </>
  );
}

// ── Inquiries tab ─────────────────────────────────────────────────────────────

function InquiriesTab() {
  const { data: inquiries, isLoading } = useAdminListInquiries();
  const { t } = useI18n();
  const [page, setPage] = useState(1);
  const [inquirySearch, setInquirySearch] = useState("");

  useEffect(() => { setPage(1); }, [inquirySearch]);

  if (isLoading) return <div className="p-8 text-center">{t("Loading...")}</div>;

  const rawInquiries = inquiries ?? [];
  const iq = inquirySearch.trim().toLowerCase();
  const allInquiries = iq
    ? rawInquiries.filter(
        (inquiry) =>
          inquiry.listingTitle?.toLowerCase().includes(iq) ||
          inquiry.name.toLowerCase().includes(iq) ||
          inquiry.email.toLowerCase().includes(iq),
      )
    : rawInquiries;
  const total = allInquiries.length;
  const pageInquiries = allInquiries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border bg-muted/30 shrink-0">
        <Input
          className="h-8 max-w-xs text-sm"
          placeholder={t("Search by listing, name, or email…")}
          value={inquirySearch}
          onChange={(e) => setInquirySearch(e.target.value)}
        />
        {iq && (
          <span className="text-xs text-muted-foreground ml-auto">
            {allInquiries.length} / {rawInquiries.length}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader className="bg-muted/50 sticky top-0">
            <TableRow>
              <TableHead>{t("Date")}</TableHead>
              <TableHead>{t("Listing")}</TableHead>
              <TableHead>{t("Customer Info")}</TableHead>
              <TableHead>{t("Message")}</TableHead>
              <TableHead>{t("Notified")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageInquiries.map((inquiry) => (
              <TableRow key={inquiry.id}>
                <TableCell className="whitespace-nowrap text-sm">
                  {new Date(inquiry.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="font-semibold text-sm max-w-[150px] truncate">{inquiry.listingTitle}</div>
                  <div className="text-xs text-muted-foreground">ID: {inquiry.listingId}</div>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-sm">{inquiry.name}</div>
                  <div className="text-xs text-muted-foreground">{inquiry.email}</div>
                  {inquiry.phone && (
                    <a
                      href={(() => {
                        const num = inquiry.phone.replace(/\D/g, "");
                        const msg = [
                          `Hi ${inquiry.name}, this is Souq Crypto.`,
                          `We received your inquiry about "${inquiry.listingTitle ?? "a listing"}".`,
                          inquiry.message ? `Your message: "${inquiry.message}"` : "",
                          `We'd like to help connect you with the right merchant.`,
                          "Source: Souq Crypto",
                        ].filter(Boolean).join("\n");
                        return buildWhatsAppUrl(num, msg);
                      })()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#25D366] hover:underline font-medium"
                      data-testid={`link-inquiry-whatsapp-${inquiry.id}`}
                    >
                      {inquiry.phone}
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm max-w-sm">{inquiry.message}</div>
                </TableCell>
                <TableCell>
                  {("webhookSentAt" in inquiry && inquiry.webhookSentAt) ? (
                    <Badge variant="outline" className="border-green-500 text-green-600 text-xs">
                      {t("Notified")}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-400 text-amber-600 text-xs">
                      {t("Webhook Pending")}
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {allInquiries.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {t("No inquiries found.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPage={setPage} />
    </>
  );
}

// ── Companies tab ─────────────────────────────────────────────────────────────

type AdminMerchantRow = {
  id: number;
  slug: string;
  name: string;
  area: string;
  categoryId: number;
  categoryName: string;
  description: string;
  imageUrl: string;
  logoUrl: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  website: string | null;
  instagram: string | null;
  openingHours: string | null;
  supportedNetworks: string[];
  serviceMethods: string[];
  isVerified: boolean;
  isFeatured: boolean;
  isPublished: boolean;
  listingCount?: number;
  portalAccess?: boolean;
};

type AdminMerchantListing = {
  id: number;
  title: string;
  type: string;
  categoryId: number;
  categoryName: string;
  area: string;
  imageUrl: string;
  images: string[];
  priceAed: number | null;
  isFeatured: boolean;
  isPublished: boolean;
  createdAt: string;
};

type AdminMerchantDetail = AdminMerchantRow & { listings: AdminMerchantListing[] };

type AdminInvitation = {
  id: number;
  email: string;
  companyName: string;
  merchantId: number | null;
  status: "pending" | "accepted" | "expired";
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
};

function CompaniesTab() {
  const { t } = useI18n();
  const logout = useAdminLogout();
  const [merchants, setMerchants] = useState<AdminMerchantRow[]>([]);
  const [invitations, setInvitations] = useState<AdminInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [openMerchantId, setOpenMerchantId] = useState<number | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const loadMerchants = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch("/api/admin/merchants", { credentials: "same-origin" });
      if (res.status === 401) { void logout(); return; }
      if (!res.ok) throw new Error();
      setMerchants(await res.json() as AdminMerchantRow[]);
    } catch {
      // keep previous data
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  const loadInvitations = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/invitations", { credentials: "same-origin" });
      if (res.ok) setInvitations(await res.json() as AdminInvitation[]);
    } catch { /* non-critical — ignore */ }
  }, []);

  useEffect(() => { void loadMerchants(); }, [loadMerchants]);
  useEffect(() => { void loadInvitations(); }, [loadInvitations]);
  useEffect(() => { setPage(1); }, [search]);

  if (openMerchantId !== null) {
    return (
      <MerchantDetailPanel
        merchantId={openMerchantId}
        onBack={() => { setOpenMerchantId(null); void loadMerchants(); }}
      />
    );
  }

  if (isLoading) return <div className="p-8 text-center">{t("Loading...")}</div>;

  const q = search.trim().toLowerCase();
  const filtered = merchants.filter((m) =>
    !q || m.name.toLowerCase().includes(q) || m.area.toLowerCase().includes(q) || m.categoryName.toLowerCase().includes(q));
  const total = filtered.length;
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pendingInvites = invitations.filter((inv) => inv.status === "pending").length;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border bg-muted/30 shrink-0">
        <Input
          className="h-8 max-w-xs text-sm"
          placeholder={t("Search companies…")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="input-companies-search"
        />
        <Button
          size="sm"
          className="gap-1"
          onClick={() => setShowCreateDialog(true)}
          data-testid="btn-add-company"
        >
          <Plus className="h-4 w-4" />
          {t("Add company")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={() => setShowInviteDialog(true)}
          data-testid="btn-invite-company"
        >
          <Mail className="h-4 w-4" />
          {t("Invite")}
          {pendingInvites > 0 && (
            <span className="ms-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] text-primary-foreground">
              {pendingInvites}
            </span>
          )}
        </Button>
        <span className="text-xs text-muted-foreground ms-auto">{total} {t("companies")}</span>
      </div>
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Company")}</TableHead>
              <TableHead>{t("Category")}</TableHead>
              <TableHead>{t("Area")}</TableHead>
              <TableHead>{t("Listings")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((m) => (
              <TableRow key={m.id} data-testid={`row-company-${m.id}`}>
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell>{m.categoryName}</TableCell>
                <TableCell>{m.area}</TableCell>
                <TableCell>{m.listingCount ?? 0}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant={m.isPublished ? "default" : "secondary"}>
                      {m.isPublished ? t("Published") : t("Unpublished")}
                    </Badge>
                    {m.isVerified && <Badge variant="outline">{t("Verified")}</Badge>}
                    {m.isFeatured && <Badge variant="outline">{t("Featured")}</Badge>}
                    {m.portalAccess && (
                      <Badge variant="outline" className="border-green-600 text-green-700">
                        {t("Portal")}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-end">
                  <Button size="sm" variant="outline" onClick={() => setOpenMerchantId(m.id)} data-testid={`btn-open-company-${m.id}`}>
                    {t("Open")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {pageRows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">{t("No companies found")}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-2 border-t border-border px-4 py-2 shrink-0">
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">{page} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page >= Math.ceil(total / PAGE_SIZE)} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
      {showCreateDialog && (
        <CompanyCreateDialog
          onClose={() => setShowCreateDialog(false)}
          onCreated={(merchantId) => {
            setShowCreateDialog(false);
            setOpenMerchantId(merchantId);
            void loadMerchants();
          }}
        />
      )}
      {showInviteDialog && (
        <InviteCompanyDialog
          invitations={invitations}
          onClose={() => setShowInviteDialog(false)}
          onSent={() => { void loadInvitations(); }}
        />
      )}
    </>
  );
}

function CompanyCreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (merchantId: number) => void;
}) {
  const { t } = useI18n();
  const { toast } = useToast();
  const { data: categories } = useListCategories();
  const [form, setForm] = useState({
    name: "",
    categoryId: "",
    area: "",
    description: "",
    phone: "",
    whatsapp: "",
    email: "",
    website: "",
    instagram: "",
    openingHours: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [createFieldErrors, setCreateFieldErrors] = useState<Record<string, string>>({});

  const setField = (key: keyof typeof form, value: string) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    if (createFieldErrors[key]) {
      setCreateFieldErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const validateCreateContactFields = (f: typeof form): Record<string, string> => {
    const errors: Record<string, string> = {};
    const w = f.website.trim();
    if (w) {
      try {
        const url = new URL(w);
        if (url.protocol !== "http:" && url.protocol !== "https:") {
          errors.website = t("Must be a valid URL starting with http:// or https://");
        }
      } catch { errors.website = t("Must be a valid URL starting with http:// or https://"); }
    }
    const e = f.email.trim();
    if (e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      errors.email = t("Must be a valid email address");
    }
    const phoneRe = /^\+?(?=.*\d)[\d\s\-().]{7,30}$/;
    if (f.phone.trim() && !phoneRe.test(f.phone.trim())) {
      errors.phone = t("Must be a plausible phone number (e.g. +971 50 123 4567)");
    }
    if (f.whatsapp.trim() && !phoneRe.test(f.whatsapp.trim())) {
      errors.whatsapp = t("Must be a plausible phone number (e.g. +971 50 123 4567)");
    }
    return errors;
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.categoryId || !form.area.trim() || !form.description.trim()) {
      toast({ title: t("Name, category, area, and description are required"), variant: "destructive" });
      return;
    }
    const clientErrors = validateCreateContactFields(form);
    if (Object.keys(clientErrors).length > 0) {
      setCreateFieldErrors(clientErrors);
      return;
    }
    setCreateFieldErrors({});

    setIsSaving(true);
    try {
      const response = await fetch("/api/admin/merchants", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          categoryId: Number(form.categoryId),
          area: form.area.trim(),
          description: form.description.trim(),
          phone: form.phone.trim() || null,
          whatsapp: form.whatsapp.trim() || null,
          email: form.email.trim() || null,
          website: form.website.trim() || null,
          instagram: form.instagram.trim() || null,
          openingHours: form.openingHours.trim() || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({})) as { error?: string; fieldErrors?: Record<string, string> };
        if (error.fieldErrors && Object.keys(error.fieldErrors).length > 0) {
          setCreateFieldErrors(error.fieldErrors);
          toast({ title: t("Please fix the highlighted fields"), variant: "destructive" });
        } else {
          throw new Error(error.error || t("Failed to create company"));
        }
        return;
      }

      const created = await response.json() as { id: number };
      toast({ title: t("Company created"), description: t("The company was created as unpublished.") });
      onCreated(created.id);
    } catch (error) {
      toast({
        title: t("Failed to create company"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const textField = (key: keyof typeof form, label: string, required = false) => {
    const err = createFieldErrors[key];
    return (
      <div className="space-y-1.5">
        <label className="text-sm font-medium" htmlFor={`new-company-${key}`}>
          {label}{required ? " *" : ""}
        </label>
        <Input
          id={`new-company-${key}`}
          value={form[key]}
          onChange={(event) => setField(key, event.target.value)}
          data-testid={`input-new-company-${key}`}
          className={err ? "border-destructive focus-visible:ring-destructive" : undefined}
          aria-invalid={!!err}
          aria-describedby={err ? `new-company-${key}-error` : undefined}
        />
        {err && (
          <p id={`new-company-${key}-error`} className="text-xs text-destructive" role="alert">
            {err}
          </p>
        )}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("Add company")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {textField("name", t("Name"), true)}
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="new-company-category">{t("Category")} *</label>
              <Select value={form.categoryId} onValueChange={(value) => setField("categoryId", value)}>
                <SelectTrigger id="new-company-category" data-testid="select-new-company-category">
                  <SelectValue placeholder={t("Select category")} />
                </SelectTrigger>
                <SelectContent>
                  {(categories ?? []).map((category) => (
                    <SelectItem key={category.id} value={String(category.id)}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {textField("area", t("Area"), true)}
            {textField("phone", t("Phone"))}
            {textField("whatsapp", t("WhatsApp"))}
            {textField("email", t("Email"))}
            {textField("website", t("Website"))}
            {textField("instagram", t("Instagram"))}
            {textField("openingHours", t("Opening hours"))}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="new-company-description">{t("Description")} *</label>
            <textarea
              id="new-company-description"
              className="w-full min-h-28 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.description}
              onChange={(event) => setField("description", event.target.value)}
              data-testid="input-new-company-description"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t("New companies start unpublished. Add listings and publish the company after reviewing its details.")}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancel")}</Button>
          <Button onClick={() => { void handleCreate(); }} disabled={isSaving} data-testid="btn-save-new-company">
            {isSaving ? t("Creating…") : t("Create company")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteCompanyDialog({
  invitations,
  onClose,
  onSent,
}: {
  invitations: AdminInvitation[];
  onClose: () => void;
  onSent: () => void;
}) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    if (!email.trim() || !companyName.trim()) {
      toast({ title: t("Email and company name are required"), variant: "destructive" });
      return;
    }
    setIsSending(true);
    try {
      const res = await fetch("/api/admin/merchants/invite", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), companyName: companyName.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? t("Failed to send invitation"));
      }
      toast({ title: t("Invitation sent"), description: email.trim() });
      setEmail("");
      setCompanyName("");
      onSent();
    } catch (err) {
      toast({
        title: t("Failed to send invitation"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const statusBadge = (status: AdminInvitation["status"]) => {
    if (status === "accepted") return <Badge variant="default" className="text-xs">{t("Accepted")}</Badge>;
    if (status === "expired") return <Badge variant="secondary" className="text-xs">{t("Expired")}</Badge>;
    return <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-700">{t("Pending")}</Badge>;
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {t("Invite company")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("Send a secure invitation link. The company fills in their profile, sets a password, and gets direct portal access.")}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("Contact email")} *</label>
              <Input
                type="email"
                dir="ltr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@company.com"
                data-testid="input-invite-email"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("Company name")} *</label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={t("Business name")}
                data-testid="input-invite-company-name"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("Link expires in 7 days. Sending a new invite to the same email cancels the previous one.")}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancel")}</Button>
          <Button
            onClick={() => { void handleSend(); }}
            disabled={isSending || !email.trim() || !companyName.trim()}
            data-testid="btn-send-invite"
          >
            {isSending ? t("Sending…") : t("Send invite")}
          </Button>
        </DialogFooter>

        {invitations.length > 0 && (
          <div className="border-t pt-4 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("Recent invitations")}</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {invitations.slice(0, 20).map((inv) => (
                <div key={inv.id} className="flex items-center justify-between gap-2 text-sm py-1">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{inv.companyName}</p>
                    <p className="text-xs text-muted-foreground truncate" dir="ltr">{inv.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {statusBadge(inv.status)}
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(inv.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MerchantPortalAccessCard({ merchantId, slug, portalAccess, onChanged }: { merchantId: number; slug: string; portalAccess: boolean; onChanged: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [isWorking, setIsWorking] = useState(false);

  const setPortalPassword = async (value: string | null) => {
    setIsWorking(true);
    try {
      const res = await fetch(`/api/admin/merchants/${merchantId}/portal-password`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: value }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error);
      }
      toast({ title: value === null ? t("Portal access removed") : t("Portal password set") });
      setPassword("");
      onChanged();
    } catch (e) {
      toast({ title: t("Failed to update portal access"), description: e instanceof Error ? e.message : undefined, variant: "destructive" });
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t("Merchant portal access")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          {portalAccess ? (
            <Badge variant="default" data-testid="badge-portal-access-enabled">{t("Enabled")}</Badge>
          ) : (
            <Badge variant="secondary" data-testid="badge-portal-access-disabled">{t("No access")}</Badge>
          )}
          <span className="text-muted-foreground">
            {t("Sign-in slug:")} <code className="font-mono" dir="ltr">{slug || "—"}</code> · <span dir="ltr">/merchant-portal</span>
          </span>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="merchant-portal-password">
              {portalAccess ? t("New password (min 8 characters)") : t("Password (min 8 characters)")}
            </label>
            <Input
              id="merchant-portal-password"
              dir="ltr"
              type="text"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-64"
              data-testid="input-merchant-portal-password"
            />
          </div>
          <Button
            onClick={() => { void setPortalPassword(password); }}
            disabled={isWorking || password.length < 8}
            data-testid="btn-set-portal-password"
          >
            {portalAccess ? t("Reset password") : t("Enable access")}
          </Button>
          {portalAccess && (
            <Button
              variant="outline"
              onClick={() => { if (confirm(t("Remove this merchant's portal access?"))) void setPortalPassword(null); }}
              disabled={isWorking}
              data-testid="btn-remove-portal-access"
            >
              {t("Remove access")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MerchantDetailPanel({ merchantId, onBack }: { merchantId: number; onBack: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const logout = useAdminLogout();
  const deleteListing = useAdminDeleteListing();
  const updateListing = useAdminUpdateListing();

  const [detail, setDetail] = useState<AdminMerchantDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editingListingId, setEditingListingId] = useState<number | null>(null);
  const [profileFieldErrors, setProfileFieldErrors] = useState<Record<string, string>>({});

  // Profile form state
  const [form, setForm] = useState({
    name: "", area: "", description: "", website: "", phone: "", whatsapp: "",
    email: "", instagram: "", openingHours: "", supportedNetworks: "",
    isPublished: false, isFeatured: false, isVerified: false,
  });
  const [serviceMethods, setServiceMethods] = useState<string[]>([]);

  const loadDetail = useCallback(async () => {
    try {
      const res = await fetch(`/api/admin/merchants/${merchantId}`, { credentials: "same-origin" });
      if (res.status === 401) { void logout(); return; }
      if (!res.ok) throw new Error();
      const data = await res.json() as AdminMerchantDetail;
      setDetail(data);
      setForm({
        name: data.name,
        area: data.area,
        description: data.description ?? "",
        website: data.website ?? "",
        phone: data.phone ?? "",
        whatsapp: data.whatsapp ?? "",
        email: data.email ?? "",
        instagram: data.instagram ?? "",
        openingHours: data.openingHours ?? "",
        supportedNetworks: (data.supportedNetworks ?? []).join(", "),
        isPublished: data.isPublished,
        isFeatured: data.isFeatured,
        isVerified: data.isVerified,
      });
      setServiceMethods(data.serviceMethods ?? []);
    } catch {
      toast({ title: t("Failed to load company"), variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }, [merchantId, logout, toast, t]);

  useEffect(() => { void loadDetail(); }, [loadDetail]);

  const reloadListings = useCallback(async () => {
    const res = await fetch(`/api/admin/merchants/${merchantId}`, { credentials: "same-origin" });
    if (res.ok) setDetail(await res.json() as AdminMerchantDetail);
  }, [merchantId]);

  const setField = (key: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (typeof key === "string" && profileFieldErrors[key]) {
      setProfileFieldErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const toggleServiceMethod = (method: string) =>
    setServiceMethods((prev) => prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method]);

  const validateContactFields = (f: typeof form): Record<string, string> => {
    const errors: Record<string, string> = {};
    const w = f.website.trim();
    if (w) {
      try {
        const url = new URL(w);
        if (url.protocol !== "http:" && url.protocol !== "https:") {
          errors.website = t("Must be a valid URL starting with http:// or https://");
        }
      } catch { errors.website = t("Must be a valid URL starting with http:// or https://"); }
    }
    const e = f.email.trim();
    if (e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      errors.email = t("Must be a valid email address");
    }
    const phoneRe = /^\+?(?=.*\d)[\d\s\-().]{7,30}$/;
    if (f.phone.trim() && !phoneRe.test(f.phone.trim())) {
      errors.phone = t("Must be a plausible phone number (e.g. +971 50 123 4567)");
    }
    if (f.whatsapp.trim() && !phoneRe.test(f.whatsapp.trim())) {
      errors.whatsapp = t("Must be a plausible phone number (e.g. +971 50 123 4567)");
    }
    return errors;
  };

  const handleSaveProfile = async () => {
    if (!form.name.trim() || !form.area.trim()) {
      toast({ title: t("Name and area are required"), variant: "destructive" });
      return;
    }
    const clientErrors = validateContactFields(form);
    if (Object.keys(clientErrors).length > 0) {
      setProfileFieldErrors(clientErrors);
      return;
    }
    setProfileFieldErrors({});
    setIsSaving(true);
    try {
      const res = await fetch(`/api/admin/merchants/${merchantId}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          area: form.area.trim(),
          description: form.description.trim(),
          website: form.website.trim() || null,
          phone: form.phone.trim() || null,
          whatsapp: form.whatsapp.trim() || null,
          email: form.email.trim() || null,
          instagram: form.instagram.trim() || null,
          openingHours: form.openingHours.trim() || null,
          supportedNetworks: form.supportedNetworks.split(",").map((s) => s.trim()).filter(Boolean),
          serviceMethods,
          isPublished: form.isPublished,
          isFeatured: form.isFeatured,
          isVerified: form.isVerified,
        }),
      });
      if (res.status === 401) { void logout(); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string; fieldErrors?: Record<string, string> };
        if (err.fieldErrors && Object.keys(err.fieldErrors).length > 0) {
          setProfileFieldErrors(err.fieldErrors);
          toast({ title: t("Please fix the highlighted fields"), variant: "destructive" });
        } else {
          throw new Error(err.error);
        }
        return;
      }
      toast({ title: t("Company profile saved") });
      queryClient.invalidateQueries({ queryKey: ["/api/merchants"] });
      void loadDetail();
    } catch (e) {
      toast({ title: t("Failed to save company profile"), description: e instanceof Error ? e.message : undefined, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePublishListing = (id: number, currentVal: boolean) => {
    updateListing.mutate({ id, data: { isPublished: !currentVal } }, {
      onSuccess: () => {
        toast({ title: t("Listing updated") });
        queryClient.invalidateQueries({ queryKey: ["/api/listings"] });
        void reloadListings();
      },
    });
  };

  const handleDeleteListing = (id: number) => {
    if (!confirm(t("Are you sure you want to delete this listing?"))) return;
    deleteListing.mutate({ id }, {
      onSuccess: () => {
        toast({ title: t("Listing deleted") });
        queryClient.invalidateQueries({ queryKey: ["/api/listings"] });
        void reloadListings();
      },
    });
  };

  if (isLoading || !detail) return <div className="p-8 text-center">{t("Loading...")}</div>;

  const textField = (key: keyof typeof form, label: string, dir?: "ltr") => {
    const err = profileFieldErrors[key as string];
    return (
      <div className="space-y-1.5">
        <label className="text-sm font-medium" htmlFor={`merchant-${key}`}>{label}</label>
        <Input
          id={`merchant-${key}`}
          dir={dir}
          value={form[key] as string}
          onChange={(e) => setField(key, e.target.value)}
          data-testid={`input-merchant-${key}`}
          className={err ? "border-destructive focus-visible:ring-destructive" : undefined}
          aria-invalid={!!err}
          aria-describedby={err ? `merchant-${key}-error` : undefined}
        />
        {err && (
          <p id={`merchant-${key}-error`} className="text-xs text-destructive" role="alert">
            {err}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/30 sticky top-0 z-10">
        <Button size="sm" variant="ghost" className="gap-1" onClick={onBack} data-testid="btn-back-to-companies">
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
          {t("Companies")}
        </Button>
        <div className="min-w-0">
          <h2 className="font-semibold truncate">{detail.name}</h2>
          <p className="text-xs text-muted-foreground truncate">{detail.categoryName} · {detail.area}</p>
        </div>
      </div>

      <div className="p-4 space-y-6 max-w-4xl">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t("Company profile")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {textField("name", t("Name"))}
              {textField("area", t("Area"))}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="merchant-description">{t("Description")}</label>
              <textarea
                id="merchant-description"
                className="w-full min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                data-testid="input-merchant-description"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {textField("website", t("Website"), "ltr")}
              {textField("email", t("Email"), "ltr")}
              {textField("phone", t("Phone"), "ltr")}
              {textField("whatsapp", t("WhatsApp"), "ltr")}
              {textField("instagram", t("Instagram"), "ltr")}
              {textField("openingHours", t("Opening hours"))}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="merchant-supportedNetworks">{t("Supported networks (comma-separated)")}</label>
              <Input
                id="merchant-supportedNetworks"
                dir="ltr"
                value={form.supportedNetworks}
                onChange={(e) => setField("supportedNetworks", e.target.value)}
                placeholder="Bitcoin, Ethereum, USDT (TRC-20)"
                data-testid="input-merchant-supportedNetworks"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("Service methods")}</label>
              <div className="grid gap-2 sm:grid-cols-2">
                {SERVICE_METHOD_OPTIONS.map(([value, label]) => (
                  <label key={value} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={serviceMethods.includes(value)}
                      onChange={() => toggleServiceMethod(value)}
                      data-testid={`checkbox-merchant-method-${value}`}
                    />
                    {t(label)}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.isPublished} onCheckedChange={(v) => setField("isPublished", v)} data-testid="switch-merchant-published" />
                {t("Published")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.isVerified} onCheckedChange={(v) => setField("isVerified", v)} data-testid="switch-merchant-verified" />
                {t("Verified")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.isFeatured} onCheckedChange={(v) => setField("isFeatured", v)} data-testid="switch-merchant-featured" />
                {t("Featured")}
              </label>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => { void handleSaveProfile(); }} disabled={isSaving} data-testid="btn-save-merchant-profile">
                {isSaving ? t("Saving…") : t("Save profile")}
              </Button>
            </div>
          </CardContent>
        </Card>

        <MerchantPortalAccessCard
          merchantId={merchantId}
          slug={detail.slug}
          portalAccess={detail.portalAccess ?? false}
          onChanged={() => { void loadDetail(); }}
        />

        <Card>
          <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">{t("Services & products")} ({detail.listings.length})</CardTitle>
            <Button size="sm" className="gap-1" onClick={() => setShowCreate(true)} data-testid="btn-add-merchant-listing">
              <Plus className="h-4 w-4" />
              {t("Add listing")}
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("Photo")}</TableHead>
                  <TableHead>{t("Title")}</TableHead>
                  <TableHead>{t("Type")}</TableHead>
                  <TableHead>{t("Category")}</TableHead>
                  <TableHead>{t("Price (AED)")}</TableHead>
                  <TableHead>{t("Published")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.listings.map((l) => (
                  <TableRow key={l.id} data-testid={`row-merchant-listing-${l.id}`}>
                    <TableCell>
                      <img
                        src={l.imageUrl}
                        alt=""
                        className="h-10 w-10 rounded-md border border-border bg-muted object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.opacity = "0.3"; }}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{l.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{l.type === "service" ? t("Service") : t("Product")}</Badge>
                    </TableCell>
                    <TableCell>{l.categoryName}</TableCell>
                    <TableCell>{l.priceAed != null ? l.priceAed.toLocaleString() : "—"}</TableCell>
                    <TableCell>
                      <Switch
                        checked={l.isPublished}
                        onCheckedChange={() => handleTogglePublishListing(l.id, l.isPublished)}
                        data-testid={`switch-merchant-listing-published-${l.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingListingId(l.id)} title={t("Edit listing")} data-testid={`btn-edit-merchant-listing-${l.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" onClick={() => handleDeleteListing(l.id)} title={t("Delete listing")} data-testid={`btn-delete-merchant-listing-${l.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {detail.listings.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">{t("No listings yet. Add the company's first service or product.")}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {showCreate && (
        <ListingCreateDialog
          onClose={() => setShowCreate(false)}
          lockedMerchant={{ id: detail.id, name: detail.name, area: detail.area, categoryId: detail.categoryId, categoryName: detail.categoryName }}
          onCreated={() => { void reloadListings(); }}
        />
      )}
      {editingListingId !== null && (
        <ListingEditDialog
          listingId={editingListingId}
          onClose={() => setEditingListingId(null)}
          onSaved={() => { void reloadListings(); }}
        />
      )}
    </div>
  );
}
